import React, { useId } from "react";
import styled, { keyframes } from "styled-components";
import { SpinnerProps } from "./types";

/*
 * CADINU loader (Bez, 2026-10-02): the official CADINU mark, the exact asset the header shows (/images/brand/mark-d.svg),
 * inside a subtle spinning ring in the mark's own violet-to-blue. It replaces the drawn husky-and-moon art.
 * The outer box keeps the old spinner's footprint (size x size*1.197) so no layout moves.
 * prefers-reduced-motion: the ring stands still.
 */

export const CADINU_MARK_SRC = "/images/brand/mark-d.svg";

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Root = styled.div<{ $w: number; $h: number }>`
  position: relative;
  width: ${({ $w }) => $w}px;
  height: ${({ $h }) => $h}px;
  display: grid;
  place-items: center;

  .cdn-ring {
    position: absolute;
    width: ${({ $w }) => $w}px;
    height: ${({ $w }) => $w}px;
    top: ${({ $w, $h }) => ($h - $w) / 2}px;
    left: 0;
    animation: ${spin} 1.1s linear infinite;
    transform-origin: 50% 50%;
  }
  .cdn-mark {
    position: relative;
    width: ${({ $w }) => Math.round($w * 0.46)}px;
    height: auto;
    user-select: none;
    -webkit-user-drag: none;
  }

  @media (prefers-reduced-motion: reduce) {
    .cdn-ring {
      animation: none;
    }
  }
`;

const Spinner: React.FC<React.PropsWithChildren<SpinnerProps>> = ({ size = 128 }) => {
  const id = `cdn${useId().replace(/[^a-zA-Z0-9]/g, "")}`;
  return (
    <Root $w={size} $h={size * 1.197} role="status" aria-label="Loading" data-testid="cadinu-loader">
      <svg className="cdn-ring" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
        <defs>
          <linearGradient id={`${id}g`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#8B00FE" />
            <stop offset="1" stopColor="#1F00FE" stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* a faint full track and a brighter arc that turns around it */}
        <circle cx="50" cy="50" r="46" fill="none" stroke="#8B00FE" strokeOpacity="0.14" strokeWidth="3" />
        <path d="M50 4 A46 46 0 0 1 96 50" fill="none" stroke={`url(#${id}g)`} strokeWidth="3.5" strokeLinecap="round" />
      </svg>
      <img className="cdn-mark" src={CADINU_MARK_SRC} alt="" aria-hidden="true" draggable={false} />
    </Root>
  );
};

export default Spinner;
